// ============================================================================
// QueueContext.js — Repeaked
// Single source of truth for queue state, party state, match-found state,
// and queue-sidebar visibility. Exposes useQueue() to any descendant.
//
// All polling, joinQueue / leaveQueue API calls, and the elapsed-time
// ticker live here. Layout.js mounts <QueueProvider> and consumes via the
// hook to render the bottom bar / sidebar / nav pulse. Dashboard.js and
// every other page consume via the same hook to read queue state.
// ============================================================================

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import {
  joinQueue,
  leaveQueue,
  getQueueStatus,
  getCurrentParty,
  getNotifications,
  respondPartyInvite,
} from '../api/api';

// ---------------------------------------------------------------------------
// Constants exported for reuse by sidebar map pickers etc.
// ---------------------------------------------------------------------------
export const MODE_5V5     = '5v5';
export const MODE_WINGMAN = 'wingman';

export const MAPS_5V5 = [
  { id: 'de_dust2',    name: 'Dust 2',      img: 'dust2'    },
  { id: 'de_mirage',   name: 'Mirage',      img: 'mirage'   },
  { id: 'de_inferno',  name: 'Inferno',     img: 'inferno'  },
  { id: 'de_nuke',     name: 'Nuke',        img: 'nuke'     },
  { id: 'de_overpass', name: 'Overpass',    img: 'overpass' },
  { id: 'de_ancient',  name: 'Ancient',     img: 'ancient'  },
  { id: 'de_anubis',   name: 'Anubis',      img: 'anubis'   },
  { id: 'de_vertigo',  name: 'Vertigo',     img: 'vertigo'  },
  { id: 'de_train',    name: 'Train',       img: 'train'    },
  { id: 'de_cbble',    name: 'Cobblestone', img: 'cbble'    },
  { id: 'de_cache',    name: 'Cache',       img: 'cache'    },
  { id: 'de_tuscan',   name: 'Tuscan',      img: 'tuscan'   },
];

export const MAPS_WINGMAN = [
  { id: 'de_shortdust',  name: 'Short Dust',  img: 'shortdust' },
  { id: 'de_lake',       name: 'Lake',        img: 'lake'      },
  { id: 'de_shortnuke',  name: 'Short Nuke',  img: 'nuke'      },
  { id: 'de_inferno',    name: 'Inferno',     img: 'inferno'   },
  { id: 'de_cbble',      name: 'Cobblestone', img: 'cbble'     },
  { id: 'de_overpass',   name: 'Overpass',    img: 'overpass'  },
  { id: 'de_vertigo',    name: 'Vertigo',     img: 'vertigo'   },
  { id: 'de_train',      name: 'Train',       img: 'train'     },
  { id: 'de_canals',     name: 'Canals',      img: 'canals'    },
  { id: 'de_safehouse',  name: 'Safehouse',   img: 'safehouse' },
  { id: 'de_bank',       name: 'Bank',        img: 'bank'      },
  { id: 'de_stmarc',     name: 'St. Marc',    img: 'stmarc'    },
];

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------
const QueueContext = createContext(null);

export const useQueue = () => {
  const ctx = useContext(QueueContext);
  if (!ctx) throw new Error('useQueue must be used within a QueueProvider');
  return ctx;
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
export function QueueProvider({ children }) {
  // queue state
  const [queueMode, setQueueMode]       = useState(null); // null | '5v5' | 'wingman'
  const [queueElapsed, setQueueElapsed] = useState(0);    // seconds
  const [queueLoading, setQueueLoading] = useState(false);
  const queueStartRef                   = useRef(null);

  // preferred mode — persists between overlay opens, separate from queueMode
  // which is only set when actively queuing
  const [preferredMode, setPreferredMode] = useState(MODE_5V5);

  // selected map pool — client-side preference only, not sent to backend
  // (matchmaking endpoint doesn't accept map filters per project doc)
  const [mapPool5v5, setMapPool5v5]         = useState(() => MAPS_5V5.map(m => m.id));
  const [mapPoolWingman, setMapPoolWingman] = useState(() => MAPS_WINGMAN.map(m => m.id));

  // party
  const [party, setParty] = useState(null);

  // invites (for cross-page popups; the actual popup UI lives in Dashboard)
  const [partyInvites, setPartyInvites] = useState([]);
  const [lobbyInvites, setLobbyInvites] = useState([]);
  const dismissedRef                    = useRef(new Set());

  // sidebar / match-found UI state
  const [queueOverlayOpen, setQueueOverlayOpen] = useState(false);
  const [matchFound, setMatchFound]             = useState(null); // null | { matchId, ... }

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------
  const queueActive   = queueMode !== null;
  const activeMapPool = queueMode === MODE_WINGMAN ? mapPoolWingman : mapPool5v5;
  const activeMapList = queueMode === MODE_WINGMAN ? MAPS_WINGMAN   : MAPS_5V5;

  // ---------------------------------------------------------------------------
  // Smart refresh: queue status, party, notifications
  // ---------------------------------------------------------------------------
  const loadQueueStatus = useCallback(async () => {
    try {
      const res  = await getQueueStatus();
      const data = res?.data || res;
      if (data && data.in_queue) {
        const m = data.mode === MODE_WINGMAN ? MODE_WINGMAN : MODE_5V5;
        setQueueMode(m);
        if (!queueStartRef.current) {
          queueStartRef.current = data.joined_at
            ? new Date(data.joined_at).getTime()
            : Date.now();
        }
        // detect match-found signal from queue status payload
        if (data.match_found && data.match_id) {
          setMatchFound(prev => prev || { matchId: data.match_id });
        }
      } else {
        setQueueMode(null);
        queueStartRef.current = null;
        setQueueElapsed(0);
      }
    } catch (_) {
      // silent — endpoint occasionally 404s when not queueing
    }
  }, []);

  const loadParty = useCallback(async () => {
    try {
      const res  = await getCurrentParty();
      const data = res?.data || res;
      const nextParty = data?.party || data;
      setParty(nextParty && nextParty.id ? nextParty : null);
    } catch (_) {
      setParty(null);
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const res   = await getNotifications();
      const data  = res?.data || res;
      const list  = Array.isArray(data) ? data : data?.notifications || [];

      const partyN = list.filter(n =>
        (n.type === 'party_invite' || n.type === 'party-invite') &&
        !n.read && !dismissedRef.current.has(n.id)
      );
      const lobbyN = list.filter(n =>
        (n.type === 'lobby_invite' || n.type === 'custom_lobby_invite') &&
        !n.read && !dismissedRef.current.has(n.id)
      );
      setPartyInvites(partyN.map(n => ({ ...n, ...(n.data || {}) })));
      setLobbyInvites(lobbyN.map(n => ({ ...n, ...(n.data || {}) })));
    } catch (_) {}
  }, []);

  // Initial load + focus refresh. This keeps the app fresh after tab switches
  // without hitting social endpoints every few seconds while idle.
  useEffect(() => {
    loadQueueStatus();
    loadParty();
    loadNotifications();

    const refreshVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      loadQueueStatus();
      loadParty();
      loadNotifications();
    };

    window.addEventListener('focus', refreshVisible);
    document.addEventListener('visibilitychange', refreshVisible);
    return () => {
      window.removeEventListener('focus', refreshVisible);
      document.removeEventListener('visibilitychange', refreshVisible);
    };
  }, [loadQueueStatus, loadParty, loadNotifications]);

  // Queue status is the only short polling loop, and only while actively queued.
  useEffect(() => {
    if (!queueActive) return;
    const i = setInterval(loadQueueStatus, 3000);
    return () => clearInterval(i);
  }, [queueActive, loadQueueStatus]);

  // Slow fallback for invites/party drift until realtime/SSE is added.
  useEffect(() => {
    const i = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      loadParty();
      loadNotifications();
    }, 30000);
    return () => clearInterval(i);
  }, [loadParty, loadNotifications]);

  // elapsed ticker — only runs while queue active
  useEffect(() => {
    if (!queueActive) return;
    const i = setInterval(() => {
      if (queueStartRef.current) {
        setQueueElapsed(Math.floor((Date.now() - queueStartRef.current) / 1000));
      }
    }, 1000);
    return () => clearInterval(i);
  }, [queueActive]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------
  const startQueue = useCallback(async (mode) => {
    if (queueLoading) return;
    setQueueLoading(true);
    try {
      // Per project doc, joinQueue accepts (mode, partyId). Map pool is held
      // client-side only — endpoint doesn't accept map filters.
      await joinQueue(mode, party?.id || null);
      queueStartRef.current = Date.now();
      setQueueMode(mode);
      setPreferredMode(mode); // keep preferred mode in sync when queue starts
      setQueueElapsed(0);
    } catch (e) {
      // surface to caller via thrown error; UI shows toast / disabled state
      console.error('startQueue failed:', e);
      throw e;
    } finally {
      setQueueLoading(false);
    }
  }, [queueLoading, party]);

  const cancelQueue = useCallback(async () => {
    if (queueLoading) return;
    setQueueLoading(true);
    try {
      await leaveQueue();
      setQueueMode(null);
      queueStartRef.current = null;
      setQueueElapsed(0);
    } catch (e) {
      console.error('cancelQueue failed:', e);
    } finally {
      setQueueLoading(false);
    }
  }, [queueLoading]);

  // map pool actions
  const toggleMap = useCallback((mode, mapId) => {
    const setter = mode === MODE_WINGMAN ? setMapPoolWingman : setMapPool5v5;
    setter(prev => {
      if (prev.includes(mapId)) {
        // never allow zero maps — refuse the toggle if it would empty the pool
        if (prev.length <= 1) return prev;
        return prev.filter(id => id !== mapId);
      }
      return [...prev, mapId];
    });
  }, []);

  const setMapPool = useCallback((mode, ids) => {
    const setter = mode === MODE_WINGMAN ? setMapPoolWingman : setMapPool5v5;
    setter(ids);
  }, []);

  // sidebar
  const openQueueOverlay  = useCallback(() => setQueueOverlayOpen(true),  []);
  const closeQueueOverlay = useCallback(() => setQueueOverlayOpen(false), []);

  // match found
  const dismissMatchFound = useCallback(() => setMatchFound(null), []);

  // invites
  const acceptPartyInvite = useCallback(async (inviteId) => {
    try {
      await respondPartyInvite(inviteId, true);
      dismissedRef.current.add(inviteId);
      setPartyInvites(prev => prev.filter(i => i.id !== inviteId));
      loadParty();
    } catch (e) {
      console.error('acceptPartyInvite failed:', e);
    }
  }, [loadParty]);

  const declinePartyInvite = useCallback(async (inviteId) => {
    try {
      await respondPartyInvite(inviteId, false);
      dismissedRef.current.add(inviteId);
      setPartyInvites(prev => prev.filter(i => i.id !== inviteId));
    } catch (e) {
      console.error('declinePartyInvite failed:', e);
    }
  }, []);

  const dismissPartyInvite = useCallback((inviteId) => {
    dismissedRef.current.add(inviteId);
    setPartyInvites(prev => prev.filter(i => i.id !== inviteId));
  }, []);

  const dismissLobbyInvite = useCallback((inviteId) => {
    dismissedRef.current.add(inviteId);
    setLobbyInvites(prev => prev.filter(i => i.id !== inviteId));
  }, []);

  // ---------------------------------------------------------------------------
  // Provider value
  // ---------------------------------------------------------------------------
  const value = {
    // queue
    queueMode,
    queueActive,
    preferredMode,
    setPreferredMode,
    queueElapsed,
    queueLoading,
    startQueue,
    cancelQueue,

    // map pool
    mapPool5v5,
    mapPoolWingman,
    activeMapPool,
    activeMapList,
    toggleMap,
    setMapPool,

    // party
    party,
    reloadParty: loadParty,
    reloadNotifications: loadNotifications,

    // sidebar visibility
    queueOverlayOpen,
    openQueueOverlay,
    closeQueueOverlay,

    // match found
    matchFound,
    dismissMatchFound,

    // invites
    partyInvites,
    lobbyInvites,
    acceptPartyInvite,
    declinePartyInvite,
    dismissPartyInvite,
    dismissLobbyInvite,
  };

  return (
    <QueueContext.Provider value={value}>
      {children}
    </QueueContext.Provider>
  );
}